import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { formatCurrency } from '../../../utils/formatters';
import AccountTitleDisplay from './AccountTitleDisplay';

/**
 * Mobile bottom sheet for one bank account's requirement breakdown (Phase 2).
 */
const BankAccountRequirementSheet = ({
  show,
  title,
  requiredFn,
  onClose,
  children,
}) => {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, onClose]);

  if (!show) return null;

  return createPortal(
    <div className="requirements-bank-sheet-root">
      <button
        type="button"
        className="requirements-bank-sheet-backdrop"
        aria-label="Close breakdown"
        onClick={onClose}
      />
      <div
        className="requirements-bank-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="requirements-bank-sheet-title"
      >
        <div className="requirements-bank-sheet-header">
          <div className="requirements-bank-sheet-header-text">
            <AccountTitleDisplay
              title={title}
              as="h2"
              id="requirements-bank-sheet-title"
              className="requirements-bank-sheet-title"
            />
            <div className="requirements-bank-sheet-hero">
              <span>{formatCurrency(requiredFn)}</span>
              <span className="requirements-bank-card-amount-suffix text-muted">/fn</span>
            </div>
          </div>
          <Button
            ref={closeRef}
            variant="light"
            size="sm"
            className="requirements-bank-sheet-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
        <div className="requirements-bank-sheet-body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

BankAccountRequirementSheet.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  requiredFn: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default BankAccountRequirementSheet;
